import { appConfig } from '@/shared/config/app-config'
import { clearMarkerCookie, setMarkerCookie } from './marker-cookie'

export interface Session {
  accessToken: string
  refreshToken: string
  expiresAt: number
  refreshExpiresAt: number
  // Issued by the backend at login / refresh for the mobile autologin
  // handoff (see auto-login.tsx). Absent for sessions created any other way.
  ssoToken?: string | null
}

const STORAGE_KEY = `${appConfig.storagePrefix}_session`

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Session
  } catch {
    return null
  }
}

export function saveSession(session: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  setMarkerCookie(session.refreshExpiresAt)
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY)
  clearMarkerCookie()
}

export function isSessionExpired(session: Session): boolean {
  const now = Date.now() / 1000
  return session.expiresAt <= now
}

export function isSessionExpiringSoon(
  session: Session,
  thresholdSeconds = 300,
): boolean {
  const now = Date.now() / 1000
  return session.expiresAt - now <= thresholdSeconds
}

/**
 * True when the refresh token is past its expiry, i.e. the session can no
 * longer obtain a fresh access token and is effectively logged out. An expired
 * *access* token alone does NOT count — the client refreshes it transparently.
 * Used to keep `isAuthenticated` honest so we don't fire tokenless requests on
 * a stale session blob (backend logs them as "No auth token").
 */
export function isRefreshTokenExpired(session: Session): boolean {
  const now = Date.now() / 1000
  // Mirror token-manager.refreshToken: a missing refreshExpiresAt falls back to
  // the iOS 20-day default, i.e. treat it as not-yet-expired.
  const refreshExpiresAt = session.refreshExpiresAt ?? now + 20 * 24 * 60 * 60
  return refreshExpiresAt <= now
}
